import { Component, inject, signal, computed } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { DataService } from "./data";
import { TourCard } from "./tour-card";
import { Icon } from "./icon";
import { Reveal } from "./reveal";
import { DolphinPod } from "./dolphin-pod";
import { StiltFisherman } from "./stilt-fisherman";

type SortKey = "popularity" | "price-asc" | "price-desc" | "rating";

const REGIONS = [
  "All Regions",
  "West Coast",
  "Cultural Triangle",
  "Hill Country",
  "South Coast",
  "Full Circuit",
];

@Component({
  selector: "app-tours",
  imports: [
    RouterLink,
    TourCard,
    Icon,
    Reveal,
    FormsModule,
    DolphinPod,
    StiltFisherman,
  ],
  template: `
    <section class="relative pt-40 pb-16 surface-ink overflow-hidden">
      <div class="absolute inset-0 opacity-25">
        <img
          src="./tour-galle-1.png"
          alt=""
          class="w-full h-full object-cover"
          referrerpolicy="no-referrer"
        />
        <div
          class="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40"
        ></div>
      </div>
      <app-dolphin-pod />
      <app-stilt-fisherman
        class="absolute right-[3%] bottom-0 z-[5] hidden md:block"
      />
      <div class="container relative z-10">
        <div class="flex items-center gap-2 mb-6" appReveal="fade">
          <div class="w-8 h-[2px] bg-saffron"></div>
          <span
            class="text-saffron font-bold uppercase tracking-[0.3em] text-[10px]"
            >Six Routes, One Island</span
          >
        </div>
        <h1
          class="text-6xl md:text-8xl font-serif leading-[0.9] tracking-tight"
          appReveal
          [revealDelay]="0.1"
        >
          Our <span class="italic font-light text-paper/50">Tours</span>
        </h1>
        <p
          class="max-w-xl text-paper/60 leading-relaxed text-lg mt-6"
          appReveal
          [revealDelay]="0.2"
        >
          From a single day in Colombo to a nine-day circuit of the whole island
          - every route below is priced in USD, per person, guide and vehicle
          included.
        </p>
      </div>
    </section>

    <section class="relative z-20 -mt-10">
      <div class="container">
        <div class="glass rounded-[2rem] p-6 sm:p-8" appReveal="scale">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
            <div>
              <label
                for="q"
                class="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-teal mb-2"
              >
                <app-icon name="search" [size]="13" /> Search
              </label>
              <input
                id="q"
                type="text"
                placeholder="Try 'Ella' or 'coast'"
                [(ngModel)]="query"
                class="w-full bg-transparent border-b-2 border-sand py-2 focus:outline-none focus:border-vermilion font-medium placeholder:text-ink/25"
              />
            </div>
            <div>
              <label
                for="region"
                class="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-teal mb-2"
              >
                <app-icon name="map" [size]="13" /> Region
              </label>
              <select
                id="region"
                [(ngModel)]="region"
                class="w-full bg-transparent border-b-2 border-sand py-2 focus:outline-none focus:border-vermilion font-medium appearance-none"
              >
                @for (r of regions; track r) {
                  <option [value]="r">{{ r }}</option>
                }
              </select>
            </div>
            <div>
              <label
                for="budget"
                class="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-teal mb-2"
              >
                <app-icon name="wallet" [size]="13" /> Budget (USD)
              </label>
              <select
                id="budget"
                [(ngModel)]="budget"
                class="w-full bg-transparent border-b-2 border-sand py-2 focus:outline-none focus:border-vermilion font-medium appearance-none"
              >
                <option value="all">Any budget</option>
                <option value="under300">Under &#36;300</option>
                <option value="300-700">&#36;300 – &#36;700</option>
                <option value="700plus">&#36;700+</option>
              </select>
            </div>
            <div>
              <label
                for="sort"
                class="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-teal mb-2"
              >
                <app-icon name="filter" [size]="13" /> Sort By
              </label>
              <select
                id="sort"
                [(ngModel)]="sort"
                class="w-full bg-transparent border-b-2 border-sand py-2 focus:outline-none focus:border-vermilion font-medium appearance-none"
              >
                <option value="popularity">Most Popular</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-28">
      <div class="container">
        <div class="flex items-center justify-between mb-12">
          <div class="text-ink/50 text-xs font-bold uppercase tracking-widest">
            Showing
            <span class="text-ink route-code">{{ filtered().length }}</span> of
            {{ tours().length }} routes
          </div>
          @if (query() || region() !== "All Regions" || budget() !== "all") {
            <button
              type="button"
              (click)="reset()"
              class="text-xs font-bold uppercase tracking-widest text-vermilion hover:underline underline-offset-2"
            >
              Clear Filters
            </button>
          }
        </div>

        @if (filtered().length) {
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            @for (tour of filtered(); track tour.id; let i = $index) {
              <app-tour-card
                [tour]="tour"
                appReveal
                [revealDelay]="Math.min(i, 4) * 0.08"
              />
            }
          </div>
        } @else {
          <div
            class="text-center py-24 rounded-[1.75rem] border border-dashed border-sand"
          >
            <p class="text-xl font-serif mb-2">
              No routes match those filters.
            </p>
            <p class="text-ink/50 mb-8">
              Try widening your budget or region - or tell us directly what
              you're after.
            </p>
            <button type="button" (click)="reset()" class="btn-outline mx-auto">
              Reset Filters
            </button>
          </div>
        }

        <div
          class="grain mt-28 relative overflow-hidden surface-ink rounded-[2.5rem] p-14 sm:p-20"
        >
          <div
            class="mesh-blob mesh-blob-a w-[380px] h-[380px] -top-32 -right-20 bg-saffron/15"
          ></div>
          <div class="absolute inset-0 opacity-10">
            <img
              src="./home-grid-4.png"
              alt=""
              class="w-full h-full object-cover"
              referrerpolicy="no-referrer"
            />
          </div>
          <div
            class="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10"
          >
            <div class="max-w-xl space-y-5">
              <span class="route-code text-saffron text-xs">CUSTOM ROUTE</span>
              <h2 class="text-4xl sm:text-5xl font-serif leading-tight">
                Can't find your <br /><span
                  class="italic font-light text-paper/50"
                  >perfect journey?</span
                >
              </h2>
              <p class="text-paper/60 leading-relaxed">
                Every itinerary here is a starting point. Tell us your dates,
                pace and interests and we'll route something built for you.
              </p>
            </div>
            <a routerLink="/contact" class="btn-saffron shrink-0">
              Build a Custom Trip <app-icon name="arrow-right" [size]="16" />
            </a>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class Tours {
  private dataService = inject(DataService);
  private route = inject(ActivatedRoute);
  tours = this.dataService.getTours();
  regions = REGIONS;
  Math = Math;

  query = signal("");
  region = signal("All Regions");
  budget = signal<"all" | "under300" | "300-700" | "700plus">("all");
  sort = signal<SortKey>("popularity");

  constructor() {
    const params = this.route.snapshot.queryParamMap;
    const q = params.get("q");
    if (q) this.query.set(q);
    const budget = params.get("budget");
    if (budget === "under300" || budget === "300-700" || budget === "700plus") {
      this.budget.set(budget);
    }
  }

  filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const region = this.region();
    const budget = this.budget();
    const sort = this.sort();

    let list = this.tours().filter((t) => {
      const matchesQuery =
        !q ||
        [t.title, t.location, t.region, t.tagline].some((f) =>
          f.toLowerCase().includes(q),
        );
      const matchesRegion = region === "All Regions" || t.region === region;
      const matchesBudget =
        budget === "all" ||
        (budget === "under300" && t.price < 300) ||
        (budget === "300-700" && t.price >= 300 && t.price <= 700) ||
        (budget === "700plus" && t.price > 700);
      return matchesQuery && matchesRegion && matchesBudget;
    });

    list = [...list].sort((a, b) => {
      if (sort === "price-asc") return a.price - b.price;
      if (sort === "price-desc") return b.price - a.price;
      if (sort === "rating") return b.rating - a.rating;
      return b.reviews - a.reviews;
    });

    return list;
  });

  reset() {
    this.query.set("");
    this.region.set("All Regions");
    this.budget.set("all");
  }
}
